import type { TileProduct } from "../../../components/shared/ProductTilesSearchTile";
import type { QuickFilter } from "./quickFilters";

export interface RelatedSearch {
  id: string;
  label: string;
  imageUrl: string;
  /** The query this tile runs when clicked. */
  query: string;
}

/**
 * Material and color refinement chips for the fall-accessories catalogue.
 * Whichever options match a term in the query lead the row, so retyping the
 * search visibly reorders the chips instead of showing an unrelated fixed set.
 */
const MATERIAL_FILTER_OPTIONS = [
  "Cashmere",
  "Wool",
  "Alpaca",
  "Merino",
  "Cotton",
];

const COLOR_FILTER_OPTIONS = ["Plaid", "Black", "Ivory", "Grey", "Red"];

const MAX_REFINEMENT_FILTERS = 6;

export const refinementFiltersForQuery = (query: string): QuickFilter[] => {
  const terms = queryTerms(query);
  const options = [...MATERIAL_FILTER_OPTIONS, ...COLOR_FILTER_OPTIONS];
  const matches = options.filter((option) =>
    terms.some((term) => option.toLowerCase().includes(term))
  );
  const remainder = options.filter((option) => !matches.includes(option));
  return [...matches, ...remainder]
    .slice(0, MAX_REFINEMENT_FILTERS)
    .map((label) => ({ key: `refinement-${label.toLowerCase()}`, label }));
};

/** Stable 32-bit hash, so query-derived ordering never reshuffles between renders. */
export const hashString = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(31, hash) + value.charCodeAt(index);
  }
  return Math.abs(hash);
};

/**
 * Orders the catalogue for a committed query: literal name/brand matches first
 * (best match first), then the rest of the catalogue so the grid stays full the
 * way a real results page would.
 */
export const productsForQuery = (
  products: TileProduct[],
  query: string
): TileProduct[] => {
  const terms = queryTerms(query);
  if (terms.length === 0) {
    return products;
  }

  const ranked = products.map((product, index) => ({
    product,
    index,
    score: matchScore(product, terms),
  }));
  const matches = ranked.filter((entry) => entry.score > 0);

  // The mock fall-accessories catalogue won't literally contain every query a
  // viewer types. Rotating by the query hash keeps an off-catalogue search
  // from looking frozen, instead of faking matches that aren't there.
  if (matches.length === 0) {
    return rotateBy(products, hashString(query) % products.length);
  }

  const remainder = ranked.filter((entry) => entry.score === 0);
  const sortedMatches = [...matches].sort(
    (first, second) => second.score - first.score || first.index - second.index
  );
  return [...sortedMatches, ...remainder].map((entry) => entry.product);
};

/**
 * How the viewer asked the next batch of results to be steered. `undefined`
 * means "no preference" — keep the query's own relevance order.
 */
export type ResultSteer =
  | "relevance"
  | "popular"
  | "budget"
  | "different";

export interface ResultSteerOption {
  key: ResultSteer;
  title: string;
}

export const RESULT_STEER_OPTIONS: ResultSteerOption[] = [
  { key: "relevance", title: "More relevant to query" },
  { key: "popular", title: "Popular at stores like mine" },
  { key: "budget", title: "Budget-friendly" },
  { key: "different", title: "Something different" },
];

/**
 * Reorders the pool the next "show more" batch is drawn from. `relevance`
 * is already how `productsForQuery` sorts, so it passes through untouched;
 * the rest re-sort on fields the catalogue actually carries. `different`
 * shuffles deterministically off `seed` (the batch number) so each press
 * yields a genuinely different mix rather than re-showing the same tiles.
 */
export const steerProducts = (
  products: TileProduct[],
  steer: ResultSteer | undefined,
  seed: number
): TileProduct[] => {
  if (steer === undefined || steer === "relevance") {
    return products;
  }
  if (steer === "popular") {
    return [...products].sort(
      (first, second) =>
        Number(second.isBestseller) - Number(first.isBestseller) ||
        (second.rating ?? 0) - (first.rating ?? 0)
    );
  }
  if (steer === "budget") {
    return [...products].sort((first, second) => first.price - second.price);
  }
  return [...products].sort(
    (first, second) =>
      (hashString(`${first.id}-${seed}`) % 1000) -
      (hashString(`${second.id}-${seed}`) % 1000)
  );
};

/**
 * Fibre keywords this catalogue's own product names carry, most specific
 * first — "Merino Wool Scarf" should read as merino, not wool.
 */
const MATERIAL_KEYWORDS = [
  "cashmere",
  "merino",
  "alpaca",
  "mohair",
  "wool",
  "cotton",
  "linen",
  "silk",
];

/**
 * Whether a seeded, illustrative claim applies. Set at 60% rather than the
 * one-in-three these started at: with four independent categories, thirds left
 * most tiles with nothing to say, so nearly every one fell through to the
 * origin fallback and the whole row read "Made in …". At 60% the average tile
 * has two or three real claims, which gives the pair-picker something to vary.
 */
const isSeededTrue = (seed: string): boolean => hashString(seed) % 5 < 3;

/** Read straight off the product name, so the claim matches the listing. */
const materialContentFor = (product: TileProduct): string | undefined => {
  const name = product.productName.toLowerCase();
  const match = MATERIAL_KEYWORDS.find((keyword) => name.includes(keyword));
  return match === undefined ? undefined : `100% ${match}`;
};

/**
 * Real signal where the catalogue has one: a couple of brands state
 * duties-paid in their own name. Everything else is seeded per product, since
 * there's no duties field to read.
 */
const hasNoImportDuties = (product: TileProduct): boolean =>
  /duties paid/i.test(product.brandName) ||
  isSeededTrue(`${product.id}-duties`);

/**
 * Country of manufacture. Illustrative — there's no origin field in the
 * catalogue — but seeded per brand so every product from one brand agrees
 * rather than flickering tile to tile.
 */
const MADE_IN_LOCATIONS = [
  "Made in Canada",
  "Made in the USA",
  "Made in Italy",
  "Made in the UK",
  "Made in Portugal",
];

const madeInFor = (brandName: string): string | undefined => {
  if (!isSeededTrue(`${brandName}-made-in`)) {
    return undefined;
  }
  const hash = hashString(`${brandName}-made-in`);
  return MADE_IN_LOCATIONS[(hash >>> 2) % MADE_IN_LOCATIONS.length];
};

/** Brand values a retailer might shop for — seeded per brand, same reasoning. */
const BRAND_VALUE_TAGS = [
  "Black-owned brand",
  "Woman-owned brand",
  "LGBTQ+-owned brand",
  "Family-run brand",
];

const brandValueTagFor = (brandName: string): string | undefined => {
  // Only some brands carry a values badge, not every brand.
  if (!isSeededTrue(`${brandName}-values`)) {
    return undefined;
  }
  return BRAND_VALUE_TAGS[
    (hashString(brandName) >>> 2) % BRAND_VALUE_TAGS.length
  ];
};

/**
 * 1-2 "why this is a top pick" bullets for a Top Picks tile, drawn from four
 * kinds of claim: material content, import duties, country of manufacture, and
 * brand values. Deliberately no popularity, rating, discount or shipping copy —
 * those repeat what the tile already shows just above.
 *
 * Which pair a tile gets is seeded on its id plus the query, so the row doesn't
 * repeat the same combination across tiles and a re-search reshuffles.
 */
export const rationaleBulletsForProduct = (
  product: TileProduct,
  query: string
): string[] => {
  const reasons: string[] = [];

  const material = materialContentFor(product);
  if (material !== undefined) {
    reasons.push(material);
  }
  if (hasNoImportDuties(product)) {
    reasons.push("No import duties");
  }
  const madeIn = madeInFor(product.brandName);
  if (madeIn !== undefined) {
    reasons.push(madeIn);
  }
  const brandValueTag = brandValueTagFor(product.brandName);
  if (brandValueTag !== undefined) {
    reasons.push(brandValueTag);
  }

  // Origin and duties both apply to anything, so they can stand in and keep a
  // tile from rendering an empty rule. Alternating between them stops the rare
  // fallback from making a whole row read the same way.
  if (reasons.length === 0) {
    const hash = hashString(`${product.id}-fallback`);
    return hash % 2 === 0
      ? [MADE_IN_LOCATIONS[(hash >>> 2) % MADE_IN_LOCATIONS.length]]
      : ["No import duties"];
  }
  if (reasons.length === 1) {
    return reasons;
  }

  const hash = hashString(`${product.id}-${query.trim()}`);
  const firstIndex = hash % reasons.length;
  const secondOffset = 1 + ((hash >>> 8) % (reasons.length - 1));
  const secondIndex = (firstIndex + secondOffset) % reasons.length;
  return [reasons[firstIndex], reasons[secondIndex]];
};

/**
 * Typeahead query suggestions for what has been typed so far: catalogue-level
 * queries that contain the input, topped up with modifier completions so the
 * list keeps responding as the viewer types past the seeded queries.
 */
export const suggestionsForQuery = (
  query: string,
  suggestionPool: string[],
  maxCount: number
): string[] => {
  const normalized = query.trim().toLowerCase();
  if (normalized.length === 0) {
    return [];
  }

  const suggestions: string[] = [];
  const addSuggestion = (candidate: string) => {
    if (suggestions.length < maxCount && !suggestions.includes(candidate)) {
      suggestions.push(candidate);
    }
  };

  addSuggestion(normalized);
  suggestionPool
    .filter((candidate) => candidate.toLowerCase().includes(normalized))
    .forEach(addSuggestion);
  QUERY_MODIFIERS.forEach((modifier) =>
    addSuggestion(`${normalized} ${modifier}`)
  );

  return suggestions;
};

/** Appended to a typed query when the seeded suggestion pool runs dry. */
const QUERY_MODIFIERS = [
  "womens",
  "plaid",
  "oversized",
  "merino",
  "fringe",
  "gift set",
];

const queryTerms = (query: string): string[] =>
  query
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((term) => term.length > 0);

const searchableText = (product: TileProduct): string =>
  `${product.productName} ${product.brandName}`.toLowerCase();

const matchScore = (product: TileProduct, terms: string[]): number => {
  const text = searchableText(product);
  return terms.filter((term) => text.includes(term)).length;
};

const rotateBy = <Item,>(items: Item[], offset: number): Item[] => [
  ...items.slice(offset),
  ...items.slice(0, offset),
];
